import { Component, Inject, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material';
import { FirstErrorPipe } from '@fuse/pipes/first-error/first-error.pipe';
import { DateValidation } from '@validators/date.validation';
import { isEqual } from 'lodash';
import moment from 'moment';

@Component({
  selector: 'app-dialog-scheduling-suggestions',
  templateUrl: './dialog-scheduling-suggestions.component.html',
  styleUrls: ['./dialog-scheduling-suggestions.component.scss']
})
export class DialogSchedulingSuggestionsComponent implements OnInit {

  public suggestedLimit = 3;
  public scheduleSuggestionsFormArray: FormArray;
  public scheduleSuggestionsFormArrayToSaved: FormArray;
  public form: FormGroup;
  public minDate = moment();
  public errorMessage: string;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    public matDialogRef: MatDialogRef<DialogSchedulingSuggestionsComponent>,
    private firstErrorPipe: FirstErrorPipe,
  ) { }

  public ngOnInit(): void {
    this.form = this.data;
    this.scheduleSuggestionsFormArray = this.form.get('scheduleSuggestions') as FormArray;
    this.scheduleSuggestionsFormArrayToSaved = new FormArray([]);
    const scheduleSuggestionsStatus = this.form.get('scheduleSuggestionsStatus') && this.form.get('scheduleSuggestionsStatus').value;
    const hasScheduleSuggestions = this.scheduleSuggestionsFormArray.length > 0;
    const hasScheduleSuggestionsAndScheduleSuggestionsStatus = !!(hasScheduleSuggestions && scheduleSuggestionsStatus);
    const maintenanceStatus = this.form.get('status') && this.form.get('status').value;
    const isOnReallocationDialog = this.form.get('newCompany');
    const isOnReopenDialog = this.form.get('reopenReason');
    const isCorporateAccepted = scheduleSuggestionsStatus === 'corporateAccepted';
    const isSuggestionStopped = scheduleSuggestionsStatus === 'suggestionStopped';

    if (
      Boolean(
        ( scheduleSuggestionsStatus !== 'standardSuggested' || maintenanceStatus === 'canceled' ) && hasScheduleSuggestionsAndScheduleSuggestionsStatus
      )
      &&
      (
        Boolean(
          maintenanceStatus === 'onNegotiation' || scheduleSuggestionsStatus === 'newScheduleSuggestion'
        ) ||
        Boolean(
          maintenanceStatus === 'new' &&
          scheduleSuggestionsStatus === 'corporateSuggested'
        ) ||
        Boolean(
          maintenanceStatus === 'requested' && isOnReallocationDialog
        ) ||
        Boolean(
          maintenanceStatus === 'canceled' &&
          !isCorporateAccepted &&
          !isSuggestionStopped &&
          ( isOnReallocationDialog || isOnReopenDialog )
        ) ||
        Boolean(
          maintenanceStatus === 'scheduled' && isOnReallocationDialog
        )
      ) ||
      maintenanceStatus === 'requested'
    ) {
      this.initializeScheduleSuggestionsWithSuggestions();
    } else {
      this.addSuggestion();
    }
  }

  public initializeScheduleSuggestionsWithSuggestions(): void {
    this.scheduleSuggestionsFormArray.controls.forEach((control) => {
      const { date, time } = control.value;
      this.setScheduleSuggestionsFormArrayToSaved(this.createScheduleSuggestionsFormGroup(date, time));
    });
  }

  private createScheduleSuggestionsFormGroup(date?, time?): FormGroup {
    const newScheduleSuggestionFormGroup = new FormGroup({
      date: new FormControl(date, [Validators.required, DateValidation.validate]),
      time: new FormControl(time)
    }, {
      validators: [this.retroativeTimeValidator.bind(this), this.validateDuplicateValues.bind(this)]
    });

    if (
      (this.form.get('companyScheduleSuggestion') &&
        this.form.get('companyScheduleSuggestion').value) &&
      (!this.form.get('newCompany') &&
        this.form.get('status') &&
        this.form.get('status').value === 'onNegotiation')
    ) {
      const existingValidators = newScheduleSuggestionFormGroup.validator;
      const companyAlreadySuggestedValidator = this.companyAlreadySuggestedValidator.bind(this);
      const combinedValidators = Validators.compose([existingValidators, companyAlreadySuggestedValidator]);
      newScheduleSuggestionFormGroup.setValidators(combinedValidators);
      newScheduleSuggestionFormGroup.updateValueAndValidity();
    }

    return newScheduleSuggestionFormGroup;
  }

  private setScheduleSuggestionsFormArrayToSaved(scheduleSuggestion: FormGroup): void {
    this.scheduleSuggestionsFormArrayToSaved.push(scheduleSuggestion);

    scheduleSuggestion.valueChanges.subscribe(() => {
      this.updateStatusFromAllScheduleSuggestions();
    });
    this.updateStatusFromAllScheduleSuggestions();
  }

  private updateStatusFromAllScheduleSuggestions(): void {
    let hasErrorMessage = false;

    this.scheduleSuggestionsFormArrayToSaved.controls.forEach((formGroup) => {
      formGroup.updateValueAndValidity({ onlySelf: true, emitEvent: false });
      const errorMessage = this.firstErrorPipe.transform(formGroup as AbstractControl);
      formGroup['errorMessage'] = errorMessage;

      if (formGroup['errorMessage']) {
        hasErrorMessage = true;
      }
    });

    if (hasErrorMessage) {
      this.scheduleSuggestionsFormArrayToSaved.setErrors({ 'hasErrorMessage': true });
    } else {
      this.scheduleSuggestionsFormArrayToSaved.setErrors(null);
    }
  }

  private retroativeTimeValidator(formGroup: FormGroup): ValidationErrors | null {
    const currentDate = moment();
    const dateString = moment(formGroup.get('date').value).format('YYYY-MM-DD');
    const time = formGroup.get('time').value;
    const selectedDateTime = moment(`${dateString} ${time}`, 'YYYY-MM-DD HH:mm');
    const isDateToday = dateString === moment(currentDate).format('YYYY-MM-DD');

    if (isDateToday && !time) {
      return null;
    }

    if (selectedDateTime.isBefore(currentDate)) {
      return {
        timeOutOfRange: true
      };
    }
    return null;
  }

  private validateDuplicateValues(formGroup: FormGroup): ValidationErrors | null {
    const formArray = this.scheduleSuggestionsFormArrayToSaved;

    for (let i = 0; i < formArray.length; i++) {
      const currentFormGroup = formArray.at(i) as FormGroup;

      if (currentFormGroup === formGroup) {
        continue;
      }

      if (isEqual(currentFormGroup.value, formGroup.value)) {
        return {
          equalSchedulingSuggestion: true
        };
      }
    }

    return null;
  }

  private companyAlreadySuggestedValidator(formGroup: FormGroup): ValidationErrors | null {
    const dateControl = formGroup.get('date') as FormGroup;
    const timeControl = formGroup.get('time') as FormGroup;

    const inputTime = moment(timeControl.value, 'HH:mm');
    const inputDate = moment(dateControl.value, 'YYYY-MM-DD');
    const companyScheduleSuggestionDate = moment(this.form.get('companyScheduleSuggestion').value.date);
    const companyScheduleSuggestionTime = moment(this.form.get('companyScheduleSuggestion').value.time, 'HH:mm');

    if (inputDate.isSame(companyScheduleSuggestionDate) && inputTime.isSame(companyScheduleSuggestionTime)) {
      return {
        scheduleAlreadySuggested: true
      };
    }

    return null;
  }

  public addSuggestion(): void {
    if (this.scheduleSuggestionsFormArrayToSaved.controls.length >= this.suggestedLimit) {
      return;
    }

    const scheduleSuggestionsFormGroup = this.createScheduleSuggestionsFormGroup();
    this.setScheduleSuggestionsFormArrayToSaved(scheduleSuggestionsFormGroup);
  }

  public deleteSuggestion(scheduleSuggestionIndex: number): void {
    this.scheduleSuggestionsFormArrayToSaved.removeAt(scheduleSuggestionIndex);
    this.updateStatusFromAllScheduleSuggestions();
  }

  public saveSchedulingSuggestions(): void {
    this.updateStatusFromAllScheduleSuggestions();
    if (this.scheduleSuggestionsFormArrayToSaved.invalid) {
      return;
    }
    this.scheduleSuggestionsFormArray.clear();
    this.scheduleSuggestionsFormArrayToSaved.controls.forEach((scheduleSuggestionValueToSave) => {
      this.scheduleSuggestionsFormArray.push(
        scheduleSuggestionValueToSave
      );
      this.form.get('scheduleSuggestionsStatus').setValue('newScheduleSuggestion');
    });

    this.matDialogRef.close(true);
  }

  public closeDialog(): void {
    this.matDialogRef.close(true);
  }
}
